import {useState, useEffect, useCallback, useMemo} from 'react';
import {EditorState, ContentState} from 'draft-js';
import {useFileAssets} from '@ace-de/eua-file-assets';
import {alfMessageChannelTypes, persistenceStates} from '@ace-de/eua-entity-types';
import config from '../../config';
import allowedAttachmentMIMETypes from '../../invoices/allowedAttachmentMIMETypes';

const mandatoryFieldSets = {
    [alfMessageChannelTypes.EMAIL]: ['toEmail', 'sender'],
    [alfMessageChannelTypes.MAIL]: ['street', 'postCode', 'city'],
    [alfMessageChannelTypes.ELLA]: [],
};

/**
 * Custom hook encapsulating shared send-message form state and handlers.
 *
 * @param {object} options
 * @param {object} options.serviceCase
 * @param {object|null} options.accountPartyContactDetails
 * @param {number} options.accountPartyContactDetailsFetchedAt
 * @param {function} options.fetchAccountPartyDetails
 * @param {function} options.resetAccountPartyContactDetails
 * @param {string} options.persistenceState
 * @param {object|null} options.selectedSearchContact
 * @param {object|null} options.memberFromAmp - AMP member data override (Modal only)
 * @param {boolean} options.shouldUseAmpMemberData - Whether to prefer AMP data (Modal only)
 * @param {Array} options.serviceCaseInvoices
 * @param {string|null} options.translateOtherLabel - Translation for "OTHER" option
 */
const useSendMessageForm = ({
    serviceCase,
    accountPartyContactDetails,
    accountPartyContactDetailsFetchedAt,
    fetchAccountPartyDetails,
    resetAccountPartyContactDetails,
    persistenceState,
    selectedSearchContact = null,
    memberFromAmp = null,
    shouldUseAmpMemberData = false,
    serviceCaseInvoices = [],
    translateOtherLabel = 'Other',
}) => {
    // -- Form State --
    const [richTextAreaContent, setRichTextAreaContent] = useState(EditorState.createEmpty());
    const [selectedRecipient, setSelectedRecipient] = useState('');
    const [selectedChannel, setSelectedChannel] = useState('');
    const [mailFormData, setMailFormData] = useState({street: '', postCode: '', city: '', country: ''});
    const [emailFormData, setEmailFormData] = useState({toEmail: '', sender: ''});
    const [subject, setSubject] = useState();
    const [selectedCategory, setSelectedCategory] = useState();
    const [selectedTemplate, setSelectedTemplate] = useState();
    const [isTaskCreationEnabled, setIsTaskCreationEnabled] = useState(false);
    const [dueDate, setDueDate] = useState('');
    const [dueTime, setDueTime] = useState('');
    const [name, setName] = useState();
    const [isDueTimeValid, setIsDueTimeValid] = useState();
    const [hasEmailError, setHasEmailError] = useState(false);
    const [loadingRecipientId, setLoadingRecipientId] = useState(null);

    const isSendingInProgress = persistenceState === persistenceStates.PENDING;

    // -- File Assets --
    const {configure: configureFileAssetsService, fileList, deleteFile} = useFileAssets({
        service: 'alfFileAssetsService',
        fileList: 'service-case-incoming-messages',
    });

    useEffect(() => {
        configureFileAssetsService({
            maxFileSize: config.MAXIMUM_FILE_ASSETS_FILE_SIZE_BYTES,
            allowedMIMETypes: allowedAttachmentMIMETypes,
        });
    }, [configureFileAssetsService]);

    // -- Validation --
    const isSendingDisabled = useMemo(() => {
        if (selectedRecipient === 'OTHER' && !name) return true;
        if (isTaskCreationEnabled && (!dueDate || !dueTime || !isDueTimeValid)) return true;
        if (!selectedChannel || !selectedRecipient || !richTextAreaContent.getCurrentContent().hasText()) return true;
        if (selectedChannel === alfMessageChannelTypes.EMAIL) {
            if (!emailFormData
                || mandatoryFieldSets[alfMessageChannelTypes.EMAIL]
                    .some(field => !emailFormData[field])
                || !!hasEmailError) return true;
        }
        if (selectedChannel === alfMessageChannelTypes.MAIL) {
            if (!mailFormData
                || mandatoryFieldSets[alfMessageChannelTypes.MAIL]
                    .some(field => !mailFormData[field])) return true;
        }
        return false;
    }, [
        selectedRecipient, name, isTaskCreationEnabled, dueDate, dueTime, isDueTimeValid,
        selectedChannel, richTextAreaContent, emailFormData, hasEmailError, mailFormData,
    ]);

    // -- Account Party Details Effect --
    // Watches the fetch-completion counter so it fires even when the result is null→null (error case).
    // Only applies data if the recipient that triggered the fetch is still selected.
    useEffect(() => {
        if (loadingRecipientId === null) return;

        if (loadingRecipientId === selectedRecipient && accountPartyContactDetails != null) {
            setMailFormData({
                street: accountPartyContactDetails.address?.street || '',
                postCode: accountPartyContactDetails.address?.postCode || '',
                city: accountPartyContactDetails.address?.city || '',
                country: accountPartyContactDetails.address?.country || '',
            });
            const email = accountPartyContactDetails.email || '';
            setEmailFormData(prev => ({...(prev || {}), toEmail: email}));
        }

        setLoadingRecipientId(null);
        resetAccountPartyContactDetails();
    // intentionally fires only on fetch completion; loadingRecipientId/selectedRecipient/
    // resetAccountPartyContactDetails are read as committed state at that point and are not needed as deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountPartyContactDetailsFetchedAt]);

    // -- Form Field Handlers --
    const handleEmailFormChange = useCallback((field, value) => {
        setEmailFormData(prev => ({...prev, [field]: value}));
    }, []);

    const handleMailFormChange = useCallback((field, value) => {
        setMailFormData(prev => ({...prev, [field]: value}));
    }, []);

    // -- Recipients --
    const recipients = useMemo(() => {
        const result = {};

        if (serviceCase?.member) {
            const memberName = serviceCase.member.personalDetails.name
                && !serviceCase.member.personalDetails.name.includes('undefined')
                ? serviceCase.member.personalDetails.name
                : [serviceCase.member.personalDetails.firstName, serviceCase.member.personalDetails.surname]
                    .filter(Boolean).join(' ');

            result[serviceCase.member.membershipNo] = {
                recipientType: 'MEMBER',
                id: serviceCase.member.membershipNo,
                name: memberName,
                contactDetails: serviceCase.member.contactDetails,
                personalDetails: serviceCase.member.personalDetails,
            };
        }

        if (selectedSearchContact) {
            result[selectedSearchContact.id] = {
                recipientType: 'PRESELECTED_ACCOUNT_PARTY',
                ...selectedSearchContact,
            };
        }

        result['OTHER'] = {
            recipientType: 'OTHER',
            id: 'OTHER',
            name: translateOtherLabel,
        };

        // AMP member data override
        if (shouldUseAmpMemberData && memberFromAmp?.membershipNo && result[memberFromAmp.membershipNo]) {
            const ampPersonalDetails = memberFromAmp.personalDetails || {};
            const ampName = ampPersonalDetails.name && !ampPersonalDetails.name.includes('undefined')
                ? ampPersonalDetails.name
                : [ampPersonalDetails.firstName, ampPersonalDetails.surname].filter(Boolean).join(' ');

            result[memberFromAmp.membershipNo] = {
                ...result[memberFromAmp.membershipNo],
                name: ampName || result[memberFromAmp.membershipNo].name,
                contactDetails: memberFromAmp.contactDetails || result[memberFromAmp.membershipNo].contactDetails,
                personalDetails: memberFromAmp.personalDetails || result[memberFromAmp.membershipNo].personalDetails,
            };
        }

        // Account parties from invoices
        if (serviceCaseInvoices?.length > 0) {
            serviceCaseInvoices.forEach(scInvoice => {
                const {accountParty} = scInvoice;
                if (accountParty && accountParty.id !== serviceCase?.member?.membershipNo) {
                    result[accountParty.id] = {recipientType: 'ACCOUNT_PARTY', ...accountParty};
                }
            });
        }

        return result;
    }, [serviceCase, selectedSearchContact, translateOtherLabel, shouldUseAmpMemberData,
        memberFromAmp, serviceCaseInvoices]);

    // -- Recipient Selection Handler --
    const handleRecipientSelect = useCallback(value => {
        if (!value) return;

        if (value === 'OTHER') {
            setMailFormData({street: '', postCode: '', city: '', country: ''});
            setEmailFormData(prev => ({...(prev || {}), toEmail: ''}));
            if (loadingRecipientId !== null) {
                setLoadingRecipientId(null);
                resetAccountPartyContactDetails();
            }
            setSelectedRecipient(value);
            return;
        }

        const selectedOption = recipients[value];

        if (selectedOption.recipientType === 'MEMBER') {
            const memberDetailsSource = shouldUseAmpMemberData ? memberFromAmp : selectedOption;
            const {contactDetails, personalDetails} = memberDetailsSource || {};
            setMailFormData({
                street: personalDetails?.address?.street || '',
                postCode: personalDetails?.address?.postCode || '',
                city: personalDetails?.address?.city || '',
                country: personalDetails?.address?.country || '',
            });
            setEmailFormData(prev => ({...(prev || {}), toEmail: contactDetails?.email || ''}));
            if (loadingRecipientId !== null) {
                setLoadingRecipientId(null);
                resetAccountPartyContactDetails();
            }
        }

        if (selectedOption.recipientType === 'PRESELECTED_ACCOUNT_PARTY') {
            setMailFormData({
                street: selectedOption.address?.street || '',
                postCode: selectedOption.address?.postCode || '',
                city: selectedOption.address?.city || '',
                country: selectedOption.address?.country || '',
            });
            setEmailFormData(prev => ({...(prev || {}), toEmail: selectedOption.email || ''}));
            if (loadingRecipientId !== null) {
                setLoadingRecipientId(null);
                resetAccountPartyContactDetails();
            }
        }

        if (selectedOption.recipientType === 'ACCOUNT_PARTY') {
            const searchQueryParams = new URLSearchParams();
            searchQueryParams.append('client', `${serviceCase?.client}`);
            searchQueryParams.append('ids', `${selectedOption.id}`);
            fetchAccountPartyDetails({searchQueryParams});
            setLoadingRecipientId(value);
            setMailFormData({street: '', postCode: '', city: '', country: ''});
            setEmailFormData(prev => ({...(prev || {}), toEmail: ''}));
        }

        setSelectedRecipient(value);
    }, [recipients, shouldUseAmpMemberData, memberFromAmp, serviceCase, fetchAccountPartyDetails,
        resetAccountPartyContactDetails, loadingRecipientId]);

    // -- Channel Selection Handler --
    const handleChannelSelection = useCallback((value, {ellaRecipientId, onEllaSelect} = {}) => {
        setSelectedChannel(value);

        if (value === alfMessageChannelTypes.ELLA) {
            setSelectedTemplate('');
            setSubject('');
            setRichTextAreaContent(EditorState.createWithContent(ContentState.createFromText('')));
            if (ellaRecipientId) {
                setSelectedRecipient(ellaRecipientId);
            }
            if (onEllaSelect) {
                onEllaSelect();
            }
        }
    }, []);

    // -- Contact Address Builder (for message send) --
    const buildContactAddress = useCallback(() => {
        if (!mailFormData || !Object.values(mailFormData).some(Boolean)) return null;
        return {
            ...mailFormData,
            formattedAddress: Object.values(mailFormData).filter(Boolean).join(','),
        };
    }, [mailFormData]);

    return {
        // State
        richTextAreaContent,
        setRichTextAreaContent,
        selectedRecipient,
        setSelectedRecipient,
        selectedChannel,
        setSelectedChannel,
        mailFormData,
        setMailFormData,
        emailFormData,
        setEmailFormData,
        subject,
        setSubject,
        selectedCategory,
        setSelectedCategory,
        selectedTemplate,
        setSelectedTemplate,
        isTaskCreationEnabled,
        setIsTaskCreationEnabled,
        dueDate,
        setDueDate,
        dueTime,
        setDueTime,
        name,
        setName,
        isDueTimeValid,
        setIsDueTimeValid,
        hasEmailError,
        setHasEmailError,
        isRecipientDataLoading: loadingRecipientId !== null,

        // Derived
        isSendingDisabled,
        isSendingInProgress,
        recipients,

        // Handlers
        handleRecipientSelect,
        handleChannelSelection,
        handleEmailFormChange,
        handleMailFormChange,
        buildContactAddress,

        // File Assets
        fileList,
        deleteFile,
    };
};

export default useSendMessageForm;
